import { AdminOption } from "@/addCompany/addCompanyDomain";
import { failure, isOk, isPending, ok, pending, Result } from "@/common/common";
import { BooleanFieldModel, OptionFieldModel } from "@/common/commonForms";
import { DCEvent, showInfo, showSuccess, UpdateStoreEvent } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { companyImportApiClient } from "./companyImportApiClient";

export { companyImportTemplateUrl } from "./companyImportApiClient";

export type ImportSheet = "SHAREHOLDER_REGISTER" | "OWNERSHIP_REGISTER" | "PARTNERSHIP_REGISTER";
export type RowRejection = {
  code: string;
  column?: string | null;
  expectedExample?: string | null;
  expectedSheet?: ImportSheet | null;
};
type RowPosition = { sheet: ImportSheet; rowNumber: number };
export type ImportRowSummary = RowPosition & { name: string; organizationNumber: string };
export type RejectedRow = RowPosition & { name: string; rejection: RowRejection };
export type ImportPreview = { ready: ImportRowSummary[]; rejected: RejectedRow[] };
export type ImportResult = { imported: ImportRowSummary[]; failed: RejectedRow[] };
export type FileRejectionCode =
  | "UNREADABLE_FILE"
  | "WRONG_TEMPLATE"
  | "UNKNOWN_SHEET"
  | "NO_ROWS"
  | "TOO_MANY_ROWS"
  | "TOO_LARGE";

export type CompanyImportState = {
  accountId: string;
  accountName: string;
  file: File;
  previewResult: Result<ImportPreview, TMessage>;
  adminsModel?: OptionFieldModel<AdminOption>;
  acceptTerms: BooleanFieldModel;
  importResult?: Result<ImportResult, TMessage>;
  refresh: DCEvent;
};

const rowRejectionCodes = new Set([
  "UNKNOWN_COUNTRY",
  "UNKNOWN_ENTITY_TYPE",
  "ENTITY_TYPE_BLOCKED",
  "INVALID_IDENTIFIER",
  "SUBDIVISION_REQUIRED",
  "INVALID_SUBDIVISION",
  "NAME_REQUIRED",
  "ALREADY_EXISTS",
  "IN_ANOTHER_ACCOUNT",
  "PARENT_NOT_IN_ACCOUNT",
  "INVALID_PARENT_OWNERSHIP",
  "NOT_OWNERSHIP_REGISTER",
  "MISSING_VALUE",
  "INVALID_COUNTRY",
  "INVALID_NUMBER",
  "INVALID_SHARES",
  "INVALID_OWNERSHIP_SHARE",
  "INVALID_DATE",
  "SUBSCRIPTION_NOT_IN_ACCOUNT",
  "SUBSCRIPTION_UNAVAILABLE",
  "WRONG_SHEET",
  "UNKNOWN_PORTFOLIO",
  "DUPLICATE_IN_FILE",
  "INVALID_PARENT_IDENTIFIER",
  "PARENT_NOT_IMPORTED",
  "PARENT_CYCLE",
  "IMPORT_FAILED",
]);

const sheetTitles: Record<ImportSheet, string> = {
  SHAREHOLDER_REGISTER: "Shareholder register",
  OWNERSHIP_REGISTER: "Ownership register",
  PARTNERSHIP_REGISTER: "Partner register",
};

export const rowKey = (row: RowPosition): string => `${row.sheet}-${row.rowNumber}`;

export const rowLabel = (row: RowPosition): TMessage => ({
  key: "company-import.row",
  params: { sheet: sheetTitles[row.sheet], rowNumber: row.rowNumber },
});

const fileRejectionCodes = new Set<string>([
  "UNREADABLE_FILE",
  "WRONG_TEMPLATE",
  "UNKNOWN_SHEET",
  "NO_ROWS",
  "TOO_MANY_ROWS",
  "TOO_LARGE",
]);

const updateCompanyImport = (partial: Partial<CompanyImportState>): UpdateStoreEvent => ({
  type: "update-store",
  data: { companyImport: (state) => state && { ...state, ...partial } },
});

export const cancelCompanyImport = (): UpdateStoreEvent => ({
  type: "update-store",
  data: { companyImport: () => undefined },
});

export const openCompanyImport = (
  accountId: string,
  accountName: string,
  file: File,
  admins: AdminOption[],
  currentUserId: string | undefined,
  refresh: DCEvent,
): DCEvent[] => [
  {
    type: "update-store",
    data: {
      companyImport: () => ({
        accountId,
        accountName,
        file,
        previewResult: pending(),
        refresh,
        acceptTerms: new BooleanFieldModel({ requiredTrue: true, value: true }),
        adminsModel:
          admins.length > 0
            ? new OptionFieldModel<AdminOption>({
                options: admins,
                selectedIds: admins.map((admin) => admin.id),
                disabledFn: (admin) => admin.id === currentUserId,
                idFn: (admin) => admin.id,
                labelFn: (admin) => ({ translated: admin.name }),
              })
            : undefined,
      }),
    },
  },
  companyImportApiClient.preview(accountId, file, {
    onSuccess: (res) => [updateCompanyImport({ previewResult: ok(res.data) })],
    onFailure: (err) => [
      updateCompanyImport({ previewResult: failure(fileRejectionMessage(err)) }),
    ],
  }),
];

export const readyCount = (state: CompanyImportState): number =>
  isOk(state.previewResult) ? state.previewResult.data.ready.length : 0;

export const toggleAllAdmins = (model: OptionFieldModel<AdminOption>): void =>
  model.isAllSelected
    ? model.setSelected(model.selected.filter((admin) => model.isDisabled(admin)))
    : model.selectAll();

export const canImport = (state: CompanyImportState): boolean =>
  readyCount(state) > 0 && state.acceptTerms.value === true && !isPending(state.importResult);

export const submitCompanyImport = (state: CompanyImportState): DCEvent[] => [
  updateCompanyImport({ importResult: pending() }),
  companyImportApiClient.import(
    state.accountId,
    state.file,
    [...(state.adminsModel?.selectedIds ?? [])],
    {
      onSuccess: (res) => [cancelCompanyImport(), importedMessage(state, res.data), state.refresh],
      onFailure: (err) => [
        updateCompanyImport({ importResult: failure(fileRejectionMessage(err)) }),
      ],
    },
  ),
];

export const rejectionMessage = (rejection: RowRejection): TMessage => {
  if (!rowRejectionCodes.has(rejection.code)) return { key: "company-import.rejection.unknown" };
  if (rejection.code === "WRONG_SHEET" && rejection.expectedSheet) {
    return {
      key: "company-import.rejection.WRONG_SHEET",
      params: { sheet: sheetTitles[rejection.expectedSheet] },
    };
  }
  if (rejection.code === "INVALID_IDENTIFIER" && rejection.expectedExample) {
    return {
      key: "company-import.rejection.INVALID_IDENTIFIER-example",
      params: { example: rejection.expectedExample },
    };
  }
  return {
    key: `company-import.rejection.${rejection.code}`,
    ...(rejection.column ? { params: { column: rejection.column } } : {}),
  } as TMessage;
};

function fileRejectionMessage(err: unknown): TMessage {
  const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
  return code && fileRejectionCodes.has(code)
    ? ({ key: `company-import.file.${code}` } as TMessage)
    : { key: "company-import.failed" };
}

function importedMessage(state: CompanyImportState, result: ImportResult): DCEvent {
  const count = result.imported.length;
  const shownInPreview = new Set(
    isOk(state.previewResult) ? state.previewResult.data.rejected.map(rowKey) : [],
  );
  const newFailures = result.failed.filter((row) => !shownInPreview.has(rowKey(row)));
  return newFailures.length === 0
    ? showSuccess({
        key: "company-import.success",
        params: { count, account: state.accountName },
      } as TMessage)
    : showInfo(
        {
          key: "company-import.partial",
          params: {
            count,
            account: state.accountName,
            failed: newFailures.length,
            details: { type: "md-list", value: newFailures.map(failureLine) },
          },
        },
        { autoHide: false },
      );
}

const failureLine = (row: RejectedRow): TMessage => ({
  key: "company-import.failed-row",
  params: { name: row.name, reason: rejectionMessage(row.rejection) },
});
